import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBattleScreen } from '@/api/battles';
import { BattleResults, CATEGORY_LABELS, entrySubtitle, teamColor } from './BattleResults';

const POLL_MS = 3500;

export function BattleProjector() {
  const { programId, battleId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setData(await getBattleScreen(battleId)); setError(''); }
    catch (requestError) { setError(requestError.message); }
  }, [battleId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Keep polling after reveal: the host can still pick the sudden death winner.
    if (!['live', 'revealed'].includes(data?.status)) return undefined;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [data?.status, load]);
  const streamUrl = data?.current?.audio?.stream_url || '';
  useEffect(() => { setPlaying(false); audioRef.current?.pause(); audioRef.current?.load(); }, [streamUrl]);

  if (!data) return <main className="battle-screen battle-screen-message" data-skin="playground"><p role={error ? 'alert' : 'status'}>{error || 'Loading battle screen...'}</p></main>;
  const current = data.current;
  const team = current?.team;
  const revealed = ['revealed', 'closed'].includes(data.status);
  return <main className="battle-screen" data-skin="playground">
    <header><button onClick={() => navigate(`/facilitator/class/${programId}`)}>Live view</button><div><span>LET THE BEAT BUILD</span><h1>{data.name}</h1></div><strong>{revealed ? 'Results' : 'Beat Battle'}</strong></header>
    {error && <p className="battle-screen-error" role="alert">{error}</p>}
    {!revealed && current ? <>
      <section className="battle-screen-now" style={{ '--team-color': teamColor(team?.color) }} aria-live="polite">
        <span>Now playing · Entry {current.position}</span>
        <h2>{entrySubtitle(current)}</h2>
        <div><strong>{team?.name}</strong><b>{current.selector_name}</b></div>
        {current.audio?.stream_url && <>
          <audio ref={audioRef} src={current.audio.stream_url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
          <button className="battle-screen-play" onClick={async () => {
            if (playing) { audioRef.current?.pause(); return; }
            try { await audioRef.current?.play(); } catch (_) { setError('Tap Play again to start the room audio.'); }
          }}>{playing ? 'Pause' : 'Play'}</button>
        </>}
        {current.audio?.type === 'youtube' && current.audio.youtube_url && <a className="battle-screen-youtube" href={current.audio.youtube_url} target="_blank" rel="noreferrer">Open on YouTube</a>}
        {current.audio?.changed_after_start && <p className="battle-screen-changed">This beat changed after the battle started</p>}
        {current.audio?.needs_takehome && <p className="battle-screen-changed">Ask the kid to tap Take it home</p>}
        <div className="battle-screen-completion" aria-label={`${current.completion.done} of ${current.completion.total} judges done`}>
          <span>Judges done {current.completion.done} of {current.completion.total}</span>
          <div>{Array.from({ length: current.completion.total }, (_, index) => <i key={index} className={index < current.completion.done ? 'done' : ''} />)}</div>
        </div>
      </section>
      <section className="battle-rubric" aria-label="Judging rubric">{Object.entries(data.rubric || {}).map(([key, description]) => <article key={key}><strong>{CATEGORY_LABELS[key]}</strong><p>{description}</p></article>)}</section>
    </> : !revealed ? <section className="battle-screen-empty"><h2>Waiting for the first entry</h2><p>The host will choose what plays next.</p></section> : <BattleResults results={data.results} />}
  </main>;
}

export default BattleProjector;
